import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { APIResponseWrapperClass, BaseMasterClass, Length, UOM, Weight } from '../interfaces/master-data.interface';
import { Country } from '../models/common/country.model';

@Injectable({
  providedIn: 'root'
})
export class SharedEndpointService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.restApiUrl}shared-endpoint`;

  /**
   * Fetches all countries for a given culture code.
   * @param cultureCode The culture code (e.g., 'en-US').
   * @returns An Observable of a list of Country objects.
   */
  findAllCountries(cultureCode: string): Observable<Country[]> {
    const params = new HttpParams().set('cultureCode', cultureCode);
    return this.http.get<Country[]>(`${this.baseUrl}/countries`, { params });
  }

  /**
   * Fetches a single country by its country code and culture code.
   * @param countryCode The ISO 3166-1 alpha-2 country code (e.g., 'US').
   * @param cultureCode The culture code (e.g., 'en-US').
   * @returns An Observable of a Country object.
   */
  findCountryByCode(countryCode: string, cultureCode: string): Observable<Country> {
    const params = new HttpParams()
      .set('countryCode', countryCode)
      .set('cultureCode', cultureCode);
    return this.http.get<Country>(`${this.baseUrl}/country`, { params });
  }

  /**
   * Fetches all Unit of Measures (UOMs) for a given culture code.
   * @param cultureCode The culture code (e.g., 'en-US').
   * @returns An Observable of a list of UOM objects.
   */
  findAllUOMs(cultureCode: string): Observable<UOM[]> {
    const params = new HttpParams().set('cultureCode', cultureCode);
    return this.http.get<UOM[]>(`${this.baseUrl}/uoms`, { params });
  }

  /**
   * Fetches all length units for a given culture code.
   * @param cultureCode The culture code (e.g., 'en-US').
   * @returns An Observable of a list of Length objects.
   */
  findAllLengths(cultureCode: string): Observable<Length[]> {
    const params = new HttpParams().set('cultureCode', cultureCode);
    return this.http.get<Length[]>(`${this.baseUrl}/lengths`, { params });
  }

  /**
   * Fetches all weight units for a given culture code.
   * @param cultureCode The culture code (e.g., 'en-US').
   * @returns An Observable of a list of Weight objects.
   */
  findAllWeights(cultureCode: string): Observable<Weight[]> {
    const params = new HttpParams().set('cultureCode', cultureCode);
    return this.http.get<Weight[]>(`${this.baseUrl}/weights`, { params });
  }

  /**
   * Fetches base master data for a given culture code.
   * @param cultureCode The culture code (e.g., 'en-US').
   * @returns An Observable of APIResponseWrapperClass containing BaseMasterClass.
   */
  findBaseMaster(cultureCode: string): Observable<APIResponseWrapperClass<BaseMasterClass>> {
    const params = new HttpParams().set('cultureCode', cultureCode);
    return this.http.get<APIResponseWrapperClass<BaseMasterClass>>(`${this.baseUrl}/base-master`, { params });
  }

  /**
   * Deletes base master data cache for a given culture code.
   * @param cultureCode The culture code (e.g., 'en-US').
   * @returns An Observable of type any (or void).
   */
  deleteBaseMasterCache(cultureCode: string): Observable<any> {
    const params = new HttpParams().set('cultureCode', cultureCode);
    return this.http.delete(`${this.baseUrl}/base-master`, { params });
  }
}
